/**
 * Utility function to convert Firebase authentication error codes 
 * into user-friendly error messages
 */

export const getAuthErrorMessage = (error) => {
  if (!error || !error.code) {
    return 'An unexpected error occurred. Please try again.';
  }

  switch (error.code) {
    case 'auth/email-already-in-use':
      return 'This email address is already registered. Please use a different email or try signing in instead.';
    
    case 'auth/weak-password':
      return 'Password is too weak. Please choose a stronger password with at least 6 characters.';
    
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support for assistance.';
    
    case 'auth/user-not-found':
      return 'No account found with this email address. Please check your email or create a new account.';
    
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again or reset your password.';
    
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please wait a moment before trying again.';
    
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection and try again.';
    
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled. Please contact support.';
    
    case 'auth/requires-recent-login':
      return 'For security reasons, please sign in again to complete this action.';
    
    case 'auth/credential-already-in-use':
      return 'This credential is already associated with a different account.';
    
    case 'auth/invalid-credential':
      return 'The provided credentials are invalid. Please try again.';
    
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with the same email but different sign-in credentials.';
    
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled. Please try again.';
    
    case 'auth/popup-blocked':
      return 'Pop-up was blocked by your browser. Please allow pop-ups and try again.';
    
    case 'auth/cancelled-popup-request':
      return 'Sign-in request was cancelled. Please try again.';
    
    case 'auth/missing-email':
      return 'Please enter your email address.';
    
    case 'auth/missing-password':
      return 'Please enter your password.';
    
    case 'auth/internal-error':
      return 'An internal error occurred. Please try again later.';
    
    default:
      // For any other Firebase auth errors, provide a generic message
      // but still log the specific error for debugging
      console.error('Unhandled Firebase auth error:', error.code, error.message);
      return 'Unable to complete the request. Please try again or contact support if the problem persists.';
  }
};

/**
 * Helper function to check if an error is a Firebase auth error
 */
export const isFirebaseAuthError = (error) => {
  return error && error.code && error.code.startsWith('auth/');
};

/**
 * Enhanced error handler that provides context-specific messages
 */
export const getContextualAuthErrorMessage = (error, context = 'general') => {
  if (!error || !error.code) {
    return 'An unexpected error occurred. Please try again.';
  }

  // Context-specific messages for better UX
  switch (context) {
    case 'registration':
      if (error.code === 'auth/email-already-in-use') {
        return 'This email is already registered. Would you like to sign in instead?';
      }
      break;
    
    case 'login':
      if (error.code === 'auth/user-not-found') {
        return 'No account found with this email. Would you like to create a new account?';
      }
      if (error.code === 'auth/wrong-password') {
        return 'Incorrect password. Please try again or use "Forgot Password" to reset it.';
      }
      break;
    
    case 'password-reset':
      if (error.code === 'auth/user-not-found') {
        return 'No account found with this email address. Please check the email and try again.';
      }
      break;
  }

  // Fall back to general error message
  return getAuthErrorMessage(error);
};
